import { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useGamification } from '../context/GamificationContext';
import { CrossPostSelector, CrossPostResults } from './CrossPostSelector';
import { 
  Send, 
  Loader2, 
  Image as ImageIcon, 
  Video, 
  Pin, 
  Lock,
  Trash2,
  Edit,
  ArrowUpRight,
  X,
  Plus,
  Upload,
  AlertCircle,
  AtSign,
  Music,
  Link2,
  Paperclip
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../lib/utils';
import UserTagInput from './UserTagInput';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { ContentAttachmentPicker, AttachmentPreviewCard } from './ContentAttachmentPicker';
import { LinkUrlInput } from './LinkEmbedCard';
import ContentLockSelector from './ContentLockSelector';
import MonetizeToggle from './MonetizeToggle';



// Media upload component for posts
function MediaUploader({ 
  mediaFiles, 
  setMediaFiles, 
  uploading, 
  setUploading,
  getAuthHeaders,
  maxFiles = 4 
}) {
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadError(null);
    
    // Check max files limit
    if (mediaFiles.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} media files allowed`);
      return;
    }

    // Validate and upload each file
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error(`Invalid file type: ${file.name}. Only images and videos allowed.`);
        continue;
      }

      // Validate file size (50MB for images, 100MB for videos)
      const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large: ${file.name}. Max ${isVideo ? '100MB' : '50MB'}`);
        continue;
      }

      // Upload the file
      await uploadFile(file);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    const fileId = `${Date.now()}-${file.name}`;
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', file.type.startsWith('video/') ? 'video' : 'image');

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: Math.min((prev[fileId] || 0) + 10, 90)
        }));
      }, 200);

      // Get auth headers without Content-Type
      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const response = await fetch(`${API}/upload/post-media`, {
        method: 'POST',
        headers,
        body: formData
      });

      clearInterval(progressInterval);

      const result = await safeJson(response);

      if (result.ok && result.data) {
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        
        // Add to media files
        setMediaFiles(prev => [...prev, {
          id: fileId,
          url: result.data.file_url,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          name: file.name,
          preview: URL.createObjectURL(file)
        }]);

        toast.success(`Uploaded: ${file.name}`);
      } else {
        setUploadError(result.error || 'Upload failed');
        toast.error(result.error || `Failed to upload ${file.name}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Upload failed. Please try again.');
      toast.error(`Failed to upload ${file.name}`);
    } finally {
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    setMediaFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const isUploading = Object.keys(uploadProgress).length > 0;

  return (
    <div className="space-y-3">
      {/* Media Preview Grid */}
      {mediaFiles.length > 0 && (
        <div className={cn(
          "grid gap-2",
          mediaFiles.length === 1 ? "grid-cols-1" :
          mediaFiles.length === 2 ? "grid-cols-2" :
          "grid-cols-2"
        )}>
          {mediaFiles.map((media, index) => (
            <div 
              key={media.id} 
              className="relative rounded-lg overflow-hidden bg-black/20 aspect-video"
            >
              {media.type === 'image' ? (
                <img 
                  src={media.preview || media.url} 
                  alt={media.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <video 
                  src={media.preview || media.url}
                  className="w-full h-full object-cover"
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 bg-background/50 hover:bg-background/70"
                onClick={() => removeFile(media.id)}
              >
                <X className="w-3 h-3" />
              </Button>
              <Badge 
                className="absolute bottom-1 left-1 text-[10px] bg-background/50"
              >
                {media.type === 'video' ? '🎥' : '📷'}
              </Badge>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="space-y-1">
              <Progress value={progress} className="h-1" />
              <p className="text-xs text-muted-foreground">Uploading... {progress}%</p>
            </div>
          ))}
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-xs text-red-400 flex items-center gap-2">
            <AlertCircle className="w-3 h-3" />
            {uploadError}
          </p>
        </div>
      )}

      {/* Add Media Button */}
      {mediaFiles.length < maxFiles && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="post-media-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="gap-2"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Add Photo/Video
            <span className="text-xs text-muted-foreground">
              ({mediaFiles.length}/{maxFiles})
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}

export function PostComposer({ onPostCreated }) {
  const { getAuthHeaders } = useAuth();
  const gamification = useGamification();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  
  // Content attachment state
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [attachment, setAttachment] = useState(null);
  
  // Link embed state
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkResolving, setLinkResolving] = useState(false);
  const [resolvedEmbed, setResolvedEmbed] = useState(null);
  
  // Cross-post state
  const [crossPostPlatforms, setCrossPostPlatforms] = useState([]);
  const [crossPostResults, setCrossPostResults] = useState(null);
  
  // Content lock state
  const [lockConfig, setLockConfig] = useState(null);
  
  // Monetization state
  const [monetizeConfig, setMonetizeConfig] = useState({
    enabled: false, tip_jar: false, pay_to_unlock: false,
    unlock_price: '', subscription_only: false, boost_after: false,
  });
  
  const [newPost, setNewPost] = useState({
    content_text: '',
    is_pinned: false,
    visibility: 'public'
  });

  useEffect(() => {
    fetchPermissions();
    fetchMyPosts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPermissions = async () => {
    try {
      const response = await fetch(`${API}/artist/posts/permissions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPosts = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { headers: getAuthHeaders() });
      if (response.ok) {
        const user = await response.json();
        // Get artist ID
        const artistRes = await fetch(`${API}/artists/me`, { headers: getAuthHeaders() });
        if (artistRes.ok) {
          const artist = await artistRes.json();
          const postsRes = await fetch(`${API}/artists/${artist.artist_id}/posts?limit=10`);
          if (postsRes.ok) {
            setPosts(await postsRes.json());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const resolveLink = async (url) => {
    if (!url || !url.trim()) return;
    setLinkResolving(true);
    try {
      const res = await fetch(`${API}/embeds/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (res.ok && data) {
        setResolvedEmbed(data);
        setLinkUrl(url.trim());
      } else {
        toast.error('Could not resolve link preview');
      }
    } catch {
      toast.error('Failed to fetch link preview');
    } finally {
      setLinkResolving(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.content_text.trim() && mediaFiles.length === 0 && !attachment && !linkUrl) {
      toast.error('Please write something, add media, attach content, or share a link');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare media URLs from uploaded files only (native uploads)
      const imageUrls = mediaFiles.filter(m => m.type === 'image').map(m => m.url);
      const videoUrls = mediaFiles.filter(m => m.type === 'video').map(m => m.url);
      
      // Build request body with attachment data
      const requestBody = {
        content_text: newPost.content_text || (linkUrl ? `${resolvedEmbed?.title || linkUrl}` : ''),
        content_image_url: imageUrls[0] || null,
        content_video_url: videoUrls[0] || null,
        media_urls: [...imageUrls, ...videoUrls],
        is_pinned: newPost.is_pinned,
        visibility: newPost.visibility,
        link_url: linkUrl || null,
      };
      
      // Add attachment data if present
      if (attachment) {
        requestBody.attachment_type = attachment.attachmentType;
        requestBody.attachment_id = attachment.attachmentId;
        requestBody.attachment_owner_id = attachment.attachmentOwnerId;
        requestBody.attachment_metadata = {
          title: attachment.attachmentTitle,
          subtitle: attachment.attachmentSubtitle,
          cover_url: attachment.attachmentCoverUrl,
          duration: attachment.attachmentDuration,
          play_url: attachment.attachmentPlayUrl,
          owner_name: attachment.attachmentOwnerName
        };
      }
      
      // Add content lock config
      if (lockConfig && lockConfig.lock_type && lockConfig.lock_type !== 'public') {
        requestBody.content_lock = lockConfig;
      }
      
      // Add monetization settings
      if (monetizeConfig.enabled) {
        requestBody.monetization_settings = {
          tip_jar_enabled: monetizeConfig.tip_jar,
          subscription_only: monetizeConfig.subscription_only,
          boost_after_post: monetizeConfig.boost_after,
        };
      }
      
      const response = await fetch(`${API}/artist/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(requestBody)
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        const post = result.data;
        
        // Add tags to the post if any
        if (taggedUsers.length > 0) {
          try {
            await fetch(`${API}/posts/${post.post_id}/tags`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({
                tagged_user_ids: taggedUsers.map(u => u.user_id)
              })
            });
          } catch (tagError) {
            console.error('Failed to add tags:', tagError);
          }
        }
        
        toast.success('Post published!');
        
        // Process gamification XP from post creation
        if (post.gamification) gamification?.processXpResult({ ...post.gamification, action: 'post_created' });

        // Cross-post to selected platforms
        if (crossPostPlatforms.length > 0 && post.post_id) {
          try {
            const cpRes = await fetch(`${API}/social/cross-post`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({
                post_id: post.post_id,
                platforms: crossPostPlatforms,
                default_caption: newPost.content_text
              })
            });
            if (cpRes.ok) {
              const cpData = await cpRes.json();
              setCrossPostResults(cpData.results || []);
              const posted = (cpData.results || []).filter(r => r.status === 'posted').length;
              const smartShare = (cpData.results || []).filter(r => r.status === 'smart_share').length;
              if (posted > 0) toast.success(`Cross-posted to ${posted} platform(s)!`);
              if (smartShare > 0) toast.info(`${smartShare} platform(s) ready to share`);
            }
          } catch (cpErr) {
            console.error('Cross-post error:', cpErr);
          }
        }
        
        setNewPost({
          content_text: '',
          is_pinned: false,
          visibility: 'public'
        });
        setMediaFiles([]);
        setTaggedUsers([]);
        setAttachment(null); // Clear attachment
        setLinkUrl('');
        setResolvedEmbed(null);
        setShowLinkInput(false);
        setPosts([post, ...posts]);
        onPostCreated?.();
      } else {
        const errorDetail = result.data?.detail || result.error;
        if (typeof errorDetail === 'object') {
          toast.error(errorDetail.message || 'Failed to create post');
        } else {
          toast.error(errorDetail || 'Failed to create post');
        }
      }
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API}/artist/posts/${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setPosts(posts.filter(p => p.post_id !== postId));
        toast.success('Post deleted');
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleTogglePin = async (postId, currentlyPinned) => {
    try {
      const response = await fetch(`${API}/artist/posts/${postId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_pinned: !currentlyPinned })
      });

      const result = await safeJson(response);

      if (result.ok) {
        setPosts(posts.map(p => ({
          ...p,
          is_pinned: p.post_id === postId ? !currentlyPinned : (currentlyPinned ? p.is_pinned : false)
        })));
        toast.success(currentlyPinned ? 'Post unpinned' : 'Post pinned');
      } else {
        toast.error(result.error || 'Failed to update post');
      }
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  if (!permissions?.is_artist) {
    return null;
  }

  if (!permissions.can_create) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <Lock className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-semibold mb-2">Posts Locked</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upgrade to Pro tier or higher to create posts and share updates with your fans.
          </p>
          <Link to="/pricing">
            <Button>
              Upgrade Now <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="post-composer">
      {/* Composer */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">What&apos;s on your mind?</h3>
        
        <Textarea
          placeholder="Share an update with your fans..."
          value={newPost.content_text}
          onChange={(e) => setNewPost({ ...newPost, content_text: e.target.value })}
          className="min-h-[100px] mb-3"
          maxLength={500}
        />
        
        <div className="text-xs text-muted-foreground text-right mb-3">
          {newPost.content_text.length}/500
        </div>

        {/* Content Attachment Preview */}
        {attachment && (
          <div className="mb-4">
            <Label className="text-sm flex items-center gap-2 mb-2">
              <Paperclip className="w-4 h-4" /> Attached Content
            </Label>
            <AttachmentPreviewCard 
              attachment={attachment}
              onRemove={() => setAttachment(null)}
              showRequest={false}
            />
          </div>
        )}

        {/* Media Upload - Always available */}
        <div className="mb-4">
          <Label className="text-sm flex items-center gap-2 mb-2">
            <ImageIcon className="w-4 h-4" /> Add Photos/Videos
          </Label>
          <MediaUploader
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
            uploading={mediaUploading}
            setUploading={setMediaUploading}
            getAuthHeaders={getAuthHeaders}
            maxFiles={4}
          />
        </div>
        
        {/* Attach Content Button */}
        {!attachment && (
          <div className="mb-4">
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setShowAttachmentPicker(true)}
              className="w-full gap-2"
              data-testid="attach-content-button"
            >
              <Music className="w-4 h-4" />
              Attach Song, Video, or Creator
            </Button>
          </div>
        )}
        
        {/* Share a Link Section */}
        <div className="mb-4">
          {!showLinkInput && !resolvedEmbed && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLinkInput(true)}
              className="w-full gap-2"
              data-testid="share-link-button"
            >
              <Link2 className="w-4 h-4" />
              Share a Link
            </Button>
          )}
          {(showLinkInput || resolvedEmbed) && (
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Share a Link
              </Label>
              <LinkUrlInput
                value={linkUrl}
                onChange={setLinkUrl}
                onResolve={resolveLink}
                resolving={linkResolving}
                resolvedEmbed={resolvedEmbed}
                onClear={() => {
                  setLinkUrl('');
                  setResolvedEmbed(null);
                  setShowLinkInput(false);
                }}
              />
            </div>
          )}
        </div>
        
        {/* Tag People */}
        <div className="mb-4">
          <Label className="text-sm flex items-center gap-2 mb-2">
            <AtSign className="w-4 h-4" /> Tag People
          </Label>
          <UserTagInput
            value={taggedUsers}
            onChange={setTaggedUsers}
            maxTags={10}
            placeholder="Tag your followers..."
          />
        </div>

        {/* Pin Option (Executive+) */}
        {permissions.can_pin && (
          <div className="flex items-center gap-2 mb-3">
            <Switch
              checked={newPost.is_pinned}
              onCheckedChange={(checked) => setNewPost({ ...newPost, is_pinned: checked })}
            />
            <Label className="text-sm flex items-center gap-1">
              <Pin className="w-4 h-4" /> Pin to top of profile
            </Label>
          </div>
        )}

        {/* Feature Locks */}
        <div className="flex flex-wrap gap-2 mb-4">
          {!permissions.can_add_image && (
            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
              <Lock className="w-3 h-3 mr-1" /> Images: Elite+
            </Badge>
          )}
          {!permissions.can_add_video && (
            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
              <Lock className="w-3 h-3 mr-1" /> Videos: Executive+
            </Badge>
          )}
          {!permissions.can_pin && (
            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
              <Lock className="w-3 h-3 mr-1" /> Pin: Executive+
            </Badge>
          )}
        </div>

        {/* Cross-Post Selector */}
        <CrossPostSelector
          onSelectPlatforms={setCrossPostPlatforms}
        />

        {/* Monetize This Content */}
        <MonetizeToggle
          monetizeConfig={monetizeConfig}
          setMonetizeConfig={setMonetizeConfig}
          lockConfig={lockConfig}
          setLockConfig={setLockConfig}
        />

        {/* Cross-Post Results */}
        {crossPostResults && crossPostResults.length > 0 && (
          <CrossPostResults results={crossPostResults} onClose={() => setCrossPostResults(null)} />
        )}

        <Button 
          onClick={handleCreatePost}
          disabled={submitting || (!newPost.content_text.trim() && mediaFiles.length === 0 && !attachment && !linkUrl)}
          className="w-full"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Post Update
        </Button>
      </Card>

      {/* My Posts */}
      {posts.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Your Posts</h3>
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post.post_id} className="p-3 rounded-lg bg-muted/30">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {post.is_pinned && (
                        <Badge variant="secondary" className="text-xs">
                          <Pin className="w-3 h-3 mr-1" /> Pinned
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm line-clamp-2">{post.content_text}</p>
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      <span>{post.likes_count || 0} likes</span>
                      <span>{post.comments_count || 0} comments</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {permissions.can_pin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleTogglePin(post.post_id, post.is_pinned)}
                      >
                        <Pin className={`w-4 h-4 ${post.is_pinned ? 'text-primary' : ''}`} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:text-red-300"
                      onClick={() => handleDeletePost(post.post_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      {/* Content Attachment Picker Dialog */}
      <ContentAttachmentPicker
        isOpen={showAttachmentPicker}
        onClose={() => setShowAttachmentPicker(false)}
        onSelect={(selectedAttachment) => setAttachment(selectedAttachment)}
        allowedTypes={['song', 'video', 'creator', 'episode', 'clip']}
      />
    </div>
  );
}
